import { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { autofill } from 'redux-form';

/**
 * usePassengerAutofill hook.
 * Absorbs the autofill toggle and first-passenger autofill logic from Passengers.js:511-538, 265-293.
 * Uses redux-form autofill to push purchaser data into passengers[0].
 * @returns {object} autofillEnabled, toggleAutofill, autofillFirstPassenger.
 */
function usePassengerAutofill() {
  const dispatch = useDispatch();
  const [autofillEnabled, setAutofillEnabled] = useState(false);

  /**
   * Autofill specific fields on the first passenger.
   * Mirrors PassengersContainer.js:242-256.
   * @param {string} firstName
   * @param {string} lastName
   * @param {string} secondLastName
   * @param {string} documentType
   * @param {string} documentId
   * @param {string} email
   */
  const autofillFirstPassenger = useCallback(
    (firstName, lastName, secondLastName, documentType, documentId, email) => {
      dispatch(autofill('passengers', 'passengers[0].firstName', firstName));
      dispatch(autofill('passengers', 'passengers[0].lastName', lastName));
      dispatch(autofill('passengers', 'passengers[0].secondLastName', secondLastName));
      dispatch(autofill('passengers', 'passengers[0].email', email));
    },
    [dispatch],
  );

  /**
   * Toggle autofill for the first passenger using purchaser data.
   * Mirrors Passengers.js:511-538.
   * @param {object} purchaser - Purchaser form values.
   */
  const toggleAutofill = useCallback(
    (purchaser) => {
      const newEnabled = !autofillEnabled;

      if (newEnabled && purchaser) {
        const {
          purchaserFirstName,
          purchaserLastName,
          purchaserSecondLastName,
          documentType,
          documentId,
          email,
        } = purchaser;
        autofillFirstPassenger(
          purchaserFirstName,
          purchaserLastName,
          purchaserSecondLastName,
          documentType,
          documentId,
          email,
        );
      } else {
        autofillFirstPassenger('', '', '', '', '', '');
      }

      setAutofillEnabled(newEnabled);
    },
    [autofillEnabled, autofillFirstPassenger],
  );

  return {
    autofillEnabled,
    toggleAutofill,
    autofillFirstPassenger,
  };
}

export default usePassengerAutofill;
